import React, { useEffect, useMemo, useState } from 'react'
import { compact, groupBy, keys, maxBy, min, orderBy, range, round, sortBy, takeRightWhile, takeWhile, uniq } from 'lodash'
import { Badge, Box, Card, CardContent, CardHeader, CircularProgress, Grid, Icon, Stack, Tooltip, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { ALERT_RED, BRANCH, DISPLAY_MODE, getBranchColor, getInstanceBranchGroup, getInstanceColor, STATE, VIGILANCE } from 'dashboard/utils/DashboardUtils'
import { darkBlue, mainBlue } from 'app/theme'
import Chip from 'common/components/Chip'
import { jobSupervisionType } from '../proptypes/DashboardTypes'
import ContextMenu from 'common/components/ContextMenu'
import { useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'
import { fetchCustomers, updateCustomer } from 'gdl/customers/CustomersSlice'
import { Circle } from '@mui/icons-material'
import ApplicationConf from 'conf/ApplicationConf'
import { aquaFetch } from 'common/utils/ActionUtils'
import { toast } from 'react-toastify'
import moment from 'moment'
import { PASTEL_COLOR } from 'common/utils/ColorUtil'
import { DEFAULT_SETTINGS_VIEW } from 'common/constants/Constants'
import { CardTable, SORT } from 'common/components/datatable/Table'
import Select from 'common/components/form/Select'
import RadioButtons from 'common/components/form/RadioButtons'
import useLocalStorage from 'common/hooks/useLocalStorage'
import JobsTable, { JOB_CATEGORIES, JOB_CATEGORY } from './JobsTable'
import { getJobTypeLabel } from 'dashboard/utils/JobFormats'

// Instance qui représente le serveur lui-même : type Infra, ou ancien nommage « datacenter NN ».
const isInfraInstance = (instance) => instance.instanceType === 'Infra' || instance.name.includes('datacenter')

const DISPLAY_MODE_OPTIONS = {
    TABLE: 'table',
    MAP: 'map',
}

const JOB_SUMMARY_LINES = [
    { category: JOB_CATEGORY.NOT_LAUNCHED, key: 'notLaunchedJobs', label: 'jobs non lancés' },
    { category: JOB_CATEGORY.ERROR, key: 'errorJobs', label: 'jobs en erreur' },
    { category: JOB_CATEGORY.TOO_LONG, key: 'tooLongJobs', label: 'jobs trop longs' },
    { category: JOB_CATEGORY.FULL_DIR, key: 'fullDirectories', label: 'répertoires pleins' },
]

const countMatchingJobs = (jobs, jobTypeFilter) => (
    jobTypeFilter ? jobs.filter(j => j.jobType === jobTypeFilter).length : jobs.length
)

const renderJobSummary = (instance, jobCategoryFilter, jobTypeFilter) => {
    if (instance.statusJob === STATE.EMPTY) return 'Non supervisé'
    const lines = JOB_SUMMARY_LINES
        .filter(l => !jobCategoryFilter || jobCategoryFilter === l.category)
        .map(l => ({ ...l, count: countMatchingJobs(instance.jobs?.[l.key] ?? [], jobTypeFilter) }))
        .filter(l => l.count > 0)
        .map(l => `${l.count} ${l.label}`)
    if (!lines.length) return 'R.A.S.'
    return lines.map(s => <div key={s}>{s}</div>)
}

const getFilteredStatusJob = (instance, jobCategoryFilter, jobTypeFilter) => {
    if (!jobCategoryFilter && !jobTypeFilter) return instance.statusJob
    if (instance.statusJob === STATE.EMPTY) return STATE.EMPTY
    const candidateKeys = jobCategoryFilter
        ? [JOB_SUMMARY_LINES.find(l => l.category === jobCategoryFilter)?.key].filter(Boolean)
        : JOB_SUMMARY_LINES.map(l => l.key)
    const hasMatch = candidateKeys.some(key => countMatchingJobs(instance.jobs?.[key] ?? [], jobTypeFilter) > 0)
    return hasMatch ? STATE.KO : STATE.OK
}

const InstanceCard = ({
    instance = {},
    services = [],
    onClick = () => { },
    displayMode,
    showAll = false,
    frontLatestVersions = [],
    jobCategoryFilter,
    jobTypeFilter,
}) => {
    const showJobCategory = (cat) => !jobCategoryFilter || jobCategoryFilter === cat
    const hasNotLaunchedMatching = countMatchingJobs(instance.jobs?.notLaunchedJobs ?? [], jobTypeFilter) > 0
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false)

    const servicesWithoutConsumer = compact(services.flatMap(s => s.allRPC?.filter(r => !r.rabbitConsumersCount)))
    const servicesWithMessages = compact(services.flatMap(s => s.allRPC?.filter(r => r.rabbitMessagesCount && r.rabbitConsumersCount)))

    const messageConsumers = !!servicesWithoutConsumer.length && [
        'Queues RPC sans consumers :\n',
        ...servicesWithoutConsumer.map(s => `- ${s.rpcName} (${s.rabbitMessagesCount} messages en attente)`),
    ] .join('\n')

    const messageWaiting = !!servicesWithMessages.length && [
        'Queues RPC avec messages en attente :\n',
        ...servicesWithMessages.map(s => `- ${s.rpcName} (${s.rabbitMessagesCount} messages en attente)`),
    ] .join('\n')

    const message = [
        instance.messageBackend,
        instance.messageFrontend,
        instance.messageDB,
        messageConsumers,
        messageWaiting,
        instance.messageJob,
    ].filter(m => !!m).join('\n\n')

    const hasNotLatestVersion = instance.frontendVersions.filter(iF => !!iF.version).some(instFront => {
        return frontLatestVersions.find(v => instFront.application === v.application)?.versions?.some(v => {
            return v.branch === instance.branch && v.version !== instFront.version
        })
    })

    // Applications front monitorées sur l'instance (observatoire / widget / portail agri).
    const hasFrontApp = (needle) => instance.frontendVersions?.some(v => v.version && v.application?.toLowerCase().includes(needle))
    const hasObservatoire = hasFrontApp('observatoire')
    const hasWidget = hasFrontApp('widget')
    const hasPortail = hasFrontApp('portail')

    // const isOK = instance.statusBackend === STATE.OK && instance.statusDB === STATE.OK && instance.statusFrontend === STATE.OK && instance.statusJob === STATE.OK
    const isNotOK = instance.statusBackend !== STATE.KO

    const customer = useMemo(() => {
        return instance.customer
    }, [instance])

    const onChangeVigilance = (vigilance) => {
        setLoading(true)
        dispatch(updateCustomer({
            ...customer,
            vigilance,
        }))
            .then(() => dispatch(fetchCustomers()))
            .then(() => setLoading(false))
    }

    if (loading) {
        return (
            <Card sx={{ display: 'flex', justifyContent: 'center', padding: '5 0' }}>
                <CircularProgress color='#000' size={20} />
            </Card>
        )
    }

    return (
        <Tooltip
            title={message ? <span style={{ whiteSpace: 'pre-wrap' }}>{message}</span> : undefined}
            enterDelay={300}
            enterNextDelay={300}
            placement='auto'
        >
            <Badge
                badgeContent=''
                invisible={!customer?.vigilance}
                sx={{
                    width: '100%',
                    '& .MuiBadge-badge': {
                        color: 'lightgreen',
                        backgroundColor: customer?.vigilance === VIGILANCE.HIGH ? 'red' : 'orange',
                    },
                }}
            >
                <Card
                    sx={{
                        backgroundColor: getInstanceColor({
                            ...instance,
                            statusBackend: servicesWithoutConsumer.length ? STATE.KO : instance.statusBackend,
                            statusFrontend: hasNotLatestVersion ? STATE.KO : instance.statusFrontend,
                            statusJob: getFilteredStatusJob(instance, jobCategoryFilter, jobTypeFilter),
                        }, displayMode),
                        backgroundImage: showJobCategory(JOB_CATEGORY.NOT_LAUNCHED) &&
                            instance?.messageJob?.includes('Jobs non lancés') &&
                            hasNotLaunchedMatching &&
                            `repeating-linear-gradient(  -45deg,  transparent,  transparent 20px,  ${ALERT_RED} 20px,  ${ALERT_RED} 40px)`,
                        cursor: 'pointer',
                        width: '100%',
                    }}
                    onClick={() => onClick({ instance: instance.name })}
                >
                    <CardContent sx={{ padding: '3', paddingBottom: '0' }}>
                        <ContextMenu
                            actions={[{
                                label: 'Accéder à l\'instance',
                                onClick: () => window.open(instance.url, '_blank'),
                                icon: 'open_in_new',
                            }, {
                                label: 'Accéder au Graylog',
                                onClick: () => {
                                    aquaFetch(ApplicationConf.instance.single(instance.instanceId)).then(inst => {
                                        const instanceName = inst?.parameters?.find(p => p.parameter === 'APPLICATION_NAME')
                                        if (instanceName?.value) {
                                            window.open(`https://graylog.aquasys.fr:8081/search?q=client%3A${instanceName.value}&rangetype=relative&from=432000`, '_blank')
                                        } else {
                                            toast.error('Paramètre manquant sur l\'instance : APPLICATION_NAME')
                                        }
                                    })
                                },
                                icon: 'bug_report',
                            }, {
                                label: 'Accéder à la fiche de l\'instance',
                                onClick: () => navigate(`/instance/${instance.instanceId}/dashboard`),
                                icon: 'info',
                            }, {
                                label: 'Accéder à la fiche du client',
                                displayed: !!customer,
                                onClick: () => navigate(`/customer/${customer.id}/dashboard`),
                                icon: 'business_center',
                            }, {
                                label: 'Passer en vigilance forte',
                                displayed: !!customer && customer?.vigilance !== VIGILANCE.HIGH,
                                onClick: () => onChangeVigilance(VIGILANCE.HIGH),
                                icon: <Circle sx={{ color: 'red', fontSize: '1rem' }} />,
                            }, {
                                label: 'Passer en vigilance moyenne',
                                displayed: !!customer && customer?.vigilance !== VIGILANCE.MEDIUM,
                                onClick: () => onChangeVigilance(VIGILANCE.MEDIUM),
                                icon: <Circle sx={{ color: 'orange', fontSize: '1rem' }} />,
                            }, {
                                label: 'Retirer la vigilance',
                                displayed: !!customer && !!customer?.vigilance,
                                onClick: () => onChangeVigilance(),
                            }]}
                        >
                            <Grid container spacing={0.5} alignItems='center' sx={{ cursor: 'pointer' }}>
                                <Grid container size={12} alignItems='center'>
                                    {instance.statusBackend !== STATE.OK && (
                                        <Grid size='auto'>
                                            <Icon sx={{ fontSize: '0.9rem' }}>thunderstorm</Icon>
                                        </Grid>
                                    )}
                                    {instance.statusDB !== STATE.OK && (
                                        <Grid size='auto'>
                                            <Icon sx={{ fontSize: '0.9rem' }}>storage</Icon>
                                        </Grid>
                                    )}
                                    {showJobCategory(JOB_CATEGORY.NOT_LAUNCHED) && instance?.messageJob?.includes('Jobs non lancés') && hasNotLaunchedMatching && (
                                        <Grid size='auto'>
                                            <Icon sx={{ fontSize: '1.1rem' }}>sync_problem</Icon>
                                        </Grid>
                                    )}
                                    {!!servicesWithoutConsumer.length && (
                                        <Grid size='auto'>
                                            <Icon sx={{ fontSize: '1.1rem' }}>cruelty_free</Icon>
                                        </Grid>
                                    )}
                                    {instance.statusFrontend !== STATE.OK && (
                                        <Grid size='auto'>
                                            <Icon sx={{ fontSize: '0.9rem' }}>desktop_access_disabled</Icon>
                                        </Grid>
                                    )}
                                    {/* {isOK && (
                                        <Grid size='auto'>
                                            <Icon sx={{ fontSize: '0.9rem' }}>wb_sunny</Icon>
                                        </Grid>
                                    )} */}
                                    <Grid size='grow' container justifyContent='space-between' alignItems='center'>
                                        <Grid container size='auto' alignItems='center' spacing={0.3} sx={{ minWidth: 0 }}>
                                            <Typography
                                                variant='text'
                                                fontWeight='bold'
                                                sx={{
                                                    color: (servicesWithMessages.length || servicesWithoutConsumer.length) ? PASTEL_COLOR.red : 'black',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {instance.name}
                                            </Typography>
                                            {hasObservatoire && (
                                                <Icon sx={{ fontSize: '0.9rem' }}>public</Icon>
                                            )}
                                            {hasWidget && (
                                                <Icon sx={{ fontSize: '0.9rem' }}>widgets</Icon>
                                            )}
                                            {hasPortail && (
                                                <Icon sx={{ fontSize: '0.9rem' }}>yard</Icon>
                                            )}
                                            {instance.hasSwagger && (
                                                <Icon sx={{ fontSize: '0.9rem' }}>rss_feed</Icon>
                                            )}
                                        </Grid>
                                        {(showAll || displayMode === DISPLAY_MODE.BACK) && (
                                            <Typography sx={{ fontWeight: isNotOK ? 'normal' : 'bold', fontSize: '12px' }}>
                                                {isNotOK ? `${round(instance.maxMemory / 1000, 1)}Go` : `${instance.nbServicesKo ?? 0} KO`}
                                            </Typography>
                                        )}
                                        {!showAll && displayMode === DISPLAY_MODE.JOB && (
                                            <Typography sx={{ fontWeight: isNotOK ? 'normal' : 'bold' }}>{instance.jobs?.countJobs ? `${instance.jobs?.countJobs} jobs` : ''}</Typography>
                                        )}
                                    </Grid>
                                </Grid>
                                {(showAll || displayMode === DISPLAY_MODE.DB) && (
                                    <Grid size={12}>
                                        <div style={{ fontSize: '0.8rem' }}>
                                            Play id: {instance.playEvolutionLastId}
                                        </div>
                                    </Grid>
                                )}
                                {(showAll || displayMode === DISPLAY_MODE.FRONT) && (
                                    <Grid size={12}>
                                        <div style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                                            {instance.frontendVersions.map(v => `${v.application} : ${v.version}\n`)}
                                        </div>
                                    </Grid>
                                )}
                                {(showAll || displayMode === DISPLAY_MODE.JOB) && (
                                    <Grid size={12}>
                                        <div style={{ fontSize: '0.8rem' }}>
                                            {renderJobSummary(instance, jobCategoryFilter, jobTypeFilter)}
                                        </div>
                                    </Grid>
                                )}
                            </Grid>
                        </ContextMenu>
                    </CardContent>
                </Card>
            </Badge>
        </Tooltip>
    )
}

InstanceCard.propTypes = {
    instance: PropTypes.shape({
        branch: PropTypes.string,
        name: PropTypes.string,
        services: PropTypes.arrayOf(PropTypes.shape({
            apiName: PropTypes.string,
            commitDate: PropTypes.string,
            deployDate: PropTypes.string,
            gitlabName: PropTypes.string,
            id: PropTypes.number,
            isDefault: PropTypes.bool,
            isRunning: PropTypes.bool,
            lastCommit: PropTypes.string,
            launchDate: PropTypes.string,
            memory: PropTypes.string,
            name: PropTypes.string,
            port: PropTypes.number,
        })),
        statusJob: PropTypes.string,
        messageJob: PropTypes.string,
        jobs: jobSupervisionType,
    }),
    onClick: PropTypes.func,
    displayMode: PropTypes.string,
    showAll: PropTypes.bool,
    services: PropTypes.arrayOf(PropTypes.shape({})),
    frontLatestVersions: PropTypes.arrayOf(PropTypes.shape({})),
    jobCategoryFilter: PropTypes.string,
    jobTypeFilter: PropTypes.string,
}

const BranchCard = ({
    branch = BRANCH.UNKNOWN,
    instances = [],
    services = [],
    onClick = () => { },
    displayMode,
    frontLatestVersions = [],
    jobCategoryFilter,
    jobTypeFilter,
}) => {
    const isIncoherent = branch === BRANCH.INCOHERENT
    const foundBranches = isIncoherent ? uniq([
        ...instances.map(i => i.branch ?? BRANCH.UNKNOWN),
        ...compact(services.map(s => s.branch)),
    ]) : []
    return (
        <Card sx={{ backgroundColor: '#eee' }}>
            <CardContent sx={{ padding: '5' }}>
                <Grid container spacing={0.5} direction='column'>
                    <CardHeader
                        title={
                            <Chip
                                label={isIncoherent ? (
                                    <Stack direction='row' spacing={0.3} alignItems='center'>
                                        <span>{branch}</span>
                                        {foundBranches.map(b => (
                                            <Tooltip key={b} title={b}>
                                                <Circle sx={{ color: getBranchColor(b), fontSize: '1rem' }} />
                                            </Tooltip>
                                        ))}
                                    </Stack>
                                ) : branch}
                                color={getBranchColor(branch)}
                                onClick={!isIncoherent && (() => onClick({ branch }))}
                                sx={isIncoherent ? { fontSize: '14px', height: '22px' } : { fontSize: '12px', height: '18px' }}
                            />
                        }
                        slotProps={{
                            title: {
                                variant: 'h6',
                                align: 'center',
                            },
                        }}
                        sx={{ cursor: 'pointer', padding: 0 }}
                    />
                    {instances.map(instance => (
                        <Grid size={12} key={instance.instanceId}>
                            <InstanceCard
                                instance={instance}
                                onClick={onClick}
                                displayMode={displayMode}
                                services={services.filter(s => instance.instanceId === s.instance.id)}
                                frontLatestVersions={frontLatestVersions}
                                jobCategoryFilter={jobCategoryFilter}
                                jobTypeFilter={jobTypeFilter}
                            />
                        </Grid>
                    ))}
                </Grid>
            </CardContent>
        </Card>
    )
}

BranchCard.propTypes = {
    branch: PropTypes.string,
    instances: PropTypes.arrayOf(PropTypes.shape({
        branch: PropTypes.string,
        name: PropTypes.string,
        services: PropTypes.arrayOf(PropTypes.shape({
            apiName: PropTypes.string,
            commitDate: PropTypes.string,
            deployDate: PropTypes.string,
            gitlabName: PropTypes.string,
            id: PropTypes.number,
            isDefault: PropTypes.bool,
            isRunning: PropTypes.bool,
            lastCommit: PropTypes.string,
            launchDate: PropTypes.string,
            memory: PropTypes.string,
            name: PropTypes.string,
            port: PropTypes.number,
        })),
    })),
    services: PropTypes.arrayOf(PropTypes.shape({})),
    frontLatestVersions: PropTypes.arrayOf(PropTypes.shape({})),
    onClick: PropTypes.func,
    displayMode: PropTypes.string,
    jobCategoryFilter: PropTypes.string,
    jobTypeFilter: PropTypes.string,
}

const DatacenterCard = ({
    datacenter = {},
    onClick = () => { },
    displayMode,
    settingsView = DEFAULT_SETTINGS_VIEW,
    frontLatestVersions = [],
    jobCategoryFilter,
    jobTypeFilter,
}) => {
    const { showDiskSpaceWarning, showSaveError, showSupervisionWarning } = settingsView

    const groupByBranch = groupBy(datacenter.instances, i => getInstanceBranchGroup(i, datacenter.services))
    const branches = sortBy(keys(groupByBranch), b => b === BRANCH.INCOHERENT)

    const warningDiskUsage = useMemo(() => {
        if (!showDiskSpaceWarning) return { color: 'transparent', tooltip: '' }
        const diskSpaceUsable = min(datacenter.services.filter(s => s.instance.server === datacenter.server && s?.supervisionValues)
            .map(s => s?.supervisionValues?.disk?.usableBytes / s?.supervisionValues?.disk?.totalBytes * 100))
        if (diskSpaceUsable <= 10) return { color: 'red', tooltip: `${round(diskSpaceUsable, 1)}% d'espace disque disponible\n` }
        if (diskSpaceUsable <= 20) return { color: 'orange', tooltip: `${round(diskSpaceUsable, 1)}% d'espace disque disponible\n` }
        return { color: 'transparent', tooltip: `${round(diskSpaceUsable, 1)}% d'espace disque disponible\n` }
    }, [datacenter.services, datacenter.server, showDiskSpaceWarning])

    // 0 si save trouvée et à jour
    // 1 si pas d'info de supervision
    // 2 si save trouvée et pas à jour
    const lastSaveError = useMemo(() => {
        const supervisionsValues = datacenter.services.filter(s => s.instance.server === datacenter.server && s?.supervisionValues).map(s => s.supervisionValues)
        if (!supervisionsValues.length) return showSupervisionWarning ? 1 : 0
        if (!showSaveError) return 0
        const lastSave = min(compact(supervisionsValues.map(s => {
            const backup = s?.backup
            const backSplit = backup?.split(' - ')
            return backSplit?.[1] ? moment(backSplit[1]?.split(' ')[0]).valueOf() : 0
        })))
        if (lastSave) {
            if (moment(lastSave) < moment().hour(0).minute(0).second(0).millisecond(0).valueOf()) {
                return 2
            }
            return 0
        }
        return 2
    }, [datacenter.services, datacenter.server, showSaveError, showSupervisionWarning])

    const hasInstances = datacenter.instances.length

    return (
        <Card
            sx={{
                backgroundColor: lastSaveError === 2 ? 'red' : (lastSaveError === 1 ? PASTEL_COLOR.orange : mainBlue),
                backgroundImage: `repeating-linear-gradient(  -45deg,  transparent,  transparent 20px,  ${warningDiskUsage.color} 20px,  ${warningDiskUsage.color} 40px)`,
                width: '100%',
            }}
        >
            <Grid container>
                <Tooltip
                    title={warningDiskUsage.tooltip || lastSaveError ?
                        <span style={{ whiteSpace: 'pre-wrap' }}>
                            {warningDiskUsage.tooltip || ''}
                            {lastSaveError === 2 ? 'Sauvegarde du jour introuvable' : (lastSaveError === 1 ? 'Pas de données de supervision' : '')}
                        </span> : undefined}
                    placement='left'
                >
                    <Grid
                        size={hasInstances ? 1.5 : 12}
                        container
                        alignItems='center'
                        justifyContent='center'
                        sx={{
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: hasInstances ? '1rem' : '0.8rem',
                            cursor: 'pointer',
                            minHeight: hasInstances ? '45px' : '20px',
                        }}
                        onClick={() => onClick({ datacenter: datacenter.server })}
                    >
                        {datacenter.infraName ? `${datacenter.server} ${datacenter.infraName}` : datacenter.server}
                    </Grid>
                </Tooltip>
                {!!hasInstances && (
                    <Grid size={10.5}>
                        <CardContent sx={{ padding: '5px', paddingLeft: '0px' }}>
                            <Stack spacing={0.5}>
                                {branches.map(branch => {
                                    const instances = groupByBranch[branch]
                                    return (
                                        <BranchCard
                                            server={datacenter.server}
                                            branch={branch}
                                            instances={instances}
                                            services={datacenter.services.filter(s => instances.some(i => i.instanceId === s.instance.id))}
                                            onClick={onClick}
                                            displayMode={displayMode}
                                            key={branch}
                                            frontLatestVersions={frontLatestVersions}
                                            jobCategoryFilter={jobCategoryFilter}
                                            jobTypeFilter={jobTypeFilter}
                                        />
                                    )
                                })}
                            </Stack>
                        </CardContent>
                    </Grid>
                )}
            </Grid>
        </Card>
    )
}

DatacenterCard.propTypes = {
    datacenter: PropTypes.shape({
        server: PropTypes.number,
        infraName: PropTypes.string,
        instances: PropTypes.arrayOf(PropTypes.shape({
            branch: PropTypes.string,
            name: PropTypes.string,
            services: PropTypes.arrayOf(PropTypes.shape({
                apiName: PropTypes.string,
                commitDate: PropTypes.string,
                deployDate: PropTypes.string,
                gitlabName: PropTypes.string,
                id: PropTypes.number,
                isDefault: PropTypes.bool,
                isRunning: PropTypes.bool,
                lastCommit: PropTypes.string,
                launchDate: PropTypes.string,
                memory: PropTypes.string,
                name: PropTypes.string,
                port: PropTypes.number,
            })),
        })),
    }),
    onClick: PropTypes.func,
    displayMode: PropTypes.string,
    settingsView: PropTypes.shape({}),
    frontLatestVersions: PropTypes.arrayOf(PropTypes.shape({})),
    jobCategoryFilter: PropTypes.string,
    jobTypeFilter: PropTypes.string,
}

const FRONT_TABLE_HEADERS = [
    { key: 'instance', value: 'Instance' },
    { key: 'application', value: 'Application' },
    { key: 'url', value: 'URL' },
    { key: 'version', value: 'Version' },
    { key: 'updateDate', value: 'Date de mise à jour' },
]

const BRANCH_DISPLAY_ORDER = [
    BRANCH.PREPROD,
    BRANCH.PROD,
    BRANCH.INTEGRATION,
    BRANCH.DEVELOP,
    BRANCH.QUALI_PREPROD,
    BRANCH.MIGRATION_QUALITE,
    BRANCH.UNKNOWN,
]

const BranchVersionsTables = ({
    instances = [],
    frontLatestVersions = [],
    onClick = () => { },
    applicationFilter,
}) => {
    const groupedByBranch = groupBy(instances, i => i.branch ?? BRANCH.UNKNOWN)
    const branchOrderIndex = (branch) => {
        const idx = BRANCH_DISPLAY_ORDER.indexOf(branch)
        return idx === -1 ? BRANCH_DISPLAY_ORDER.length : idx
    }
    const branches = orderBy(keys(groupedByBranch), branchOrderIndex)

    return (
        <Stack spacing={1.5}>
            {branches.map(branch => {
                const branchInstances = orderBy(groupedByBranch[branch], 'name')
                const rows = branchInstances.flatMap(instance => (
                    (instance.frontendVersions || [])
                        .filter(v => !applicationFilter || v.application === applicationFilter)
                        .map(v => {
                            const latest = frontLatestVersions
                                .find(fv => fv.application === v.application)
                                ?.versions?.find(lv => lv.branch === branch)
                            const isOutdated = !!v.version && !!latest && latest.version !== v.version

                            return {
                                instance: {
                                    value: instance.name,
                                    onClick: () => onClick({ instance: instance.name }),
                                    fontColor: mainBlue,
                                },
                                application: { value: v.application },
                                url: v.url ? {
                                    value: v.url,
                                    onClick: () => window.open(v.url, '_blank'),
                                    fontColor: mainBlue,
                                } : { value: '' },
                                version: {
                                    value: v.version,
                                    color: isOutdated ? PASTEL_COLOR.redLight : undefined,
                                },
                                updateDate: { value: v.majDate ? moment(v.majDate).format('DD/MM/YYYY HH:mm:ss') : '' },
                            }
                        })
                ))

                if (!rows.length) {
                    return null
                }

                const maxVersion = applicationFilter ? maxBy(rows, r => r.version.value)?.version?.value : undefined

                return (
                    <CardTable
                        key={branch}
                        title={(
                            <Stack direction='row' spacing={1} alignItems='center'>
                                <Chip
                                    label={branch}
                                    color={getBranchColor(branch)}
                                    onClick={() => onClick({ branch })}
                                />
                                {maxVersion && (
                                    <span style={{ fontSize: '1rem', color: 'white' }}>
                                        {maxVersion}
                                    </span>
                                )}
                                <span style={{ fontSize: '1rem', color: 'white' }}>
                                    ({rows.length} {rows.length > 1 ? 'instances' : 'instance'})
                                </span>
                            </Stack>
                        )}
                        color={darkBlue}
                        hideNbElements
                        rows={rows}
                        headers={FRONT_TABLE_HEADERS}
                        defaultSort={{ column: 'instance', direction: SORT.ASC }}
                        rowsPerPageDefault={50}
                    />
                )
            })}
        </Stack>
    )
}

BranchVersionsTables.propTypes = {
    instances: PropTypes.arrayOf(PropTypes.shape({})),
    frontLatestVersions: PropTypes.arrayOf(PropTypes.shape({})),
    onClick: PropTypes.func,
    applicationFilter: PropTypes.string,
}

const hasntInstances = (dt) => !dt?.instances?.length

// Les serveurs sans instance consécutifs sont regroupés par paires de demi-cards ;
// dans une suite de longueur impaire, le dernier reste en pleine largeur.
const isHalfWidthDatacenter = (datacentersList, idx) => {
    if (!hasntInstances(datacentersList[idx])) return false
    const nbEmptyBefore = takeRightWhile(datacentersList.slice(0, idx), hasntInstances).length
    const nbEmptyAfter = takeWhile(datacentersList.slice(idx + 1), hasntInstances).length
    const runLength = nbEmptyBefore + 1 + nbEmptyAfter
    const isLastOfOddRun = runLength % 2 === 1 && nbEmptyAfter === 0
    return runLength > 1 && !isLastOfOddRun
}

const KwaKeUpMap = ({
    instances = [],
    services = [],
    onClick = () => { },
    displayMode,
    settingsView = DEFAULT_SETTINGS_VIEW,
}) => {
    const {
        mapColumnsSize = DEFAULT_SETTINGS_VIEW.mapColumnsSize,
        showServsWithoutInstances = DEFAULT_SETTINGS_VIEW.showServsWithoutInstances,
        nbColumns = DEFAULT_SETTINGS_VIEW.nbColumns,
    } = settingsView
    const [frontViewMode, setFrontViewMode] = useLocalStorage('kwakeup:frontViewMode', DISPLAY_MODE_OPTIONS.TABLE)
    const [jobViewMode, setJobViewMode] = useLocalStorage('kwakeup:jobViewMode', DISPLAY_MODE_OPTIONS.MAP)
    const [jobCategoryFilter, setJobCategoryFilter] = useLocalStorage('kwakeup:jobCategoryFilter')
    const [jobTypeFilter, setJobTypeFilter] = useLocalStorage('kwakeup:jobTypeFilter')
    const [applicationFilter, setApplicationFilter] = useState('SIEAU')

    // Comptes par catégorie en tenant compte du "Type de traitement" sélectionné :
    // les options de "Type d'erreur" reflètent le traitement choisi (et inversement,
    // cf. jobTypeItems). Sans traitement sélectionné => totaux bruts.
    const jobCategoryCounts = useMemo(() => {
        const counts = JOB_SUMMARY_LINES.reduce((acc, l) => ({ ...acc, [l.category]: 0 }), {})
        instances.forEach(i => {
            JOB_SUMMARY_LINES.forEach(l => {
                counts[l.category] = counts[l.category] + countMatchingJobs(i.jobs?.[l.key] ?? [], jobTypeFilter)
            })
        })
        return counts
    }, [instances, jobTypeFilter])

    const totalJobErrors = JOB_CATEGORIES.reduce((acc, c) => acc + (jobCategoryCounts[c.value] ?? 0), 0)

    const jobCategoryItems = JOB_CATEGORIES
        .filter(c => (jobCategoryCounts[c.value] ?? 0) > 0)
        .map(c => ({ ...c, label: `${c.label} (${jobCategoryCounts[c.value]})` }))

    const jobTypeItems = useMemo(() => {
        const counts = {}
        instances.forEach(i => {
            JOB_SUMMARY_LINES
                .filter(l => !jobCategoryFilter || jobCategoryFilter === l.category)
                .forEach(l => {
                    i.jobs?.[l.key]?.forEach(j => {
                        if (j.jobType) counts[j.jobType] = (counts[j.jobType] ?? 0) + 1
                    })
                })
        })
        return Object.keys(counts)
            .map(t => ({ value: t, label: `${getJobTypeLabel(t)} (${counts[t]})` }))
            .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }))
    }, [instances, jobCategoryFilter])

    // Les deux filtres persistent (localStorage) et se filtrent mutuellement : on
    // réinitialise celui qui ne correspond plus à aucune option, sinon il continue
    // de filtrer le tableau en arrière-plan (tableau vide inexpliqué). On efface en
    // priorité le "Type de traitement" pour ne pas vider les deux sur une paire
    // incompatible persistée.
    useEffect(() => {
        if (!instances.length) return
        if (jobTypeFilter && !jobTypeItems.some(it => it.value === jobTypeFilter)) {
            setJobTypeFilter(undefined)
            return
        }
        if (jobCategoryFilter && (jobCategoryCounts[jobCategoryFilter] ?? 0) === 0) {
            setJobCategoryFilter(undefined)
        }
    }, [instances, jobTypeItems, jobCategoryCounts, jobTypeFilter, jobCategoryFilter, setJobTypeFilter, setJobCategoryFilter])

    const availableApplications = useMemo(() => (
        uniq(instances.flatMap(i => (i.frontendVersions || []).map(v => v.application))).filter(Boolean)
    ), [instances])

    const groupedVersions = useMemo(() => (
        groupBy(instances.flatMap(i => i.frontendVersions.map(v => ({ ...v, branch: i.branch }))), 'application')
    ), [instances])

    const frontLatestVersions = useMemo(() => (
        keys(groupedVersions).map(app => {
            const groupedBranchs = groupBy(groupedVersions[app], 'branch')
            return {
                application: app,
                versions: compact(keys(groupedBranchs).map(branch => maxBy(groupedBranchs[branch], 'version'))),
            }
        })
    ), [groupedVersions])

    const frontStats = useMemo(() => {
        const filtered = instances.flatMap(i => (
            (i.frontendVersions || [])
                .filter(v => !applicationFilter || v.application === applicationFilter)
                .map(v => ({ ...v, branch: i.branch ?? BRANCH.UNKNOWN, instanceName: i.name }))
        ))
        const nbInstances = uniq(filtered.map(f => f.instanceName)).length
        const nbBranches = uniq(filtered.map(f => f.branch)).length
        const outdatedItems = orderBy(
            filtered.filter(f => {
                const latest = frontLatestVersions
                    .find(fv => fv.application === f.application)
                    ?.versions?.find(lv => lv.branch === f.branch)
                return !!f.version && !!latest && latest.version !== f.version
            }),
            ['branch', 'instanceName'],
        )
        return { nbInstances, nbBranches, nbOutdated: outdatedItems.length, outdatedItems }
    }, [instances, applicationFilter, frontLatestVersions])

    const formattedDatacenters = useMemo(() => {
        const groupByServer = groupBy(instances, 'server')
        const servers = orderBy(keys(groupByServer).map(server => {
            const serverServices = services.filter(s => s.instance.server === parseInt(server))
            return {
                server: parseInt(server),
                // serveur d'infra : son nom est affiché à côté du numéro (sauf le nom générique « datacenter NN »)
                infraName: groupByServer[server].filter(i => isInfraInstance(i) && !i.name.includes('datacenter')).map(i => i.name).join(', '),
                // les instances d'infra représentent le serveur lui-même : elles ne sont pas listées
                instances: groupByServer[server].filter(i => !isInfraInstance(i)),
                services: serverServices,
                nbBranches: keys(groupBy(groupByServer[server], i => getInstanceBranchGroup(i, serverServices))).length,
            }
        }), 'server')
        return showServsWithoutInstances ? servers : servers.filter(s => !!s.instances.length)
    }, [instances, services, showServsWithoutInstances])

    const rangeColumns = range(0, Number(nbColumns))

    const columns = useMemo(() => {
        const cols = rangeColumns.map(() => [])

        formattedDatacenters.forEach(d => {
            const targetCol = cols.findIndex((col, i) => {
                const colSize = col.reduce((acc, dt) => acc + dt.instances.length + dt.nbBranches, 0)
                const nextColIsEmpty = i === cols.length - 1 || !cols[i + 1].length
                return (colSize + d.instances.length + d.nbBranches) < mapColumnsSize && nextColIsEmpty
            })

            const index = targetCol === -1 ? cols.length - 1 : targetCol
            cols[index].push(d)
        })

        return cols
    }, [formattedDatacenters, mapColumnsSize, rangeColumns])

    const isFrontMode = displayMode === DISPLAY_MODE.FRONT
    const isJobMode = displayMode === DISPLAY_MODE.JOB
    const showFrontTableView = isFrontMode && frontViewMode === DISPLAY_MODE_OPTIONS.TABLE
    const showJobTableView = isJobMode && jobViewMode === DISPLAY_MODE_OPTIONS.TABLE

    return (
        <Stack spacing={1.5}>
            {isFrontMode && (
                <Grid container alignItems='center' spacing={1}>
                    <Grid size='auto'>
                        <RadioButtons
                            label="Mode d'affichage"
                            value={frontViewMode}
                            items={[
                                { value: DISPLAY_MODE_OPTIONS.TABLE, label: 'Tableaux', icon: 'table_rows' },
                                { value: DISPLAY_MODE_OPTIONS.MAP, label: 'Carte', icon: 'grid_view' },
                            ]}
                            onChange={v => setFrontViewMode(v)}
                        />
                    </Grid>
                    {showFrontTableView && (
                        <Grid size='auto'>
                            <div style={{ width: 300 }}>
                                <Select
                                    label='Application'
                                    items={availableApplications.map(a => ({ value: a, label: a }))}
                                    value={applicationFilter}
                                    onChange={v => setApplicationFilter(v)}
                                    undefinedValue
                                />
                            </div>
                        </Grid>
                    )}
                    {showFrontTableView && (
                        <Grid size='grow'>
                            <Stack direction='row' spacing={3} alignItems='center' justifyContent='flex-end'>
                                <Stack direction='row' spacing={0.75} alignItems='center'>
                                    <Icon sx={{ fontSize: '1.5rem', color: PASTEL_COLOR.greyDark }}>dns</Icon>
                                    <Typography sx={{ fontSize: '1.1rem' }}><strong>{frontStats.nbInstances}</strong> instance{frontStats.nbInstances > 1 ? 's' : ''}</Typography>
                                </Stack>
                                <Stack direction='row' spacing={0.75} alignItems='center'>
                                    <Icon sx={{ fontSize: '1.5rem', color: PASTEL_COLOR.greyDark }}>call_split</Icon>
                                    <Typography sx={{ fontSize: '1.1rem' }}><strong>{frontStats.nbBranches}</strong> branche{frontStats.nbBranches > 1 ? 's' : ''}</Typography>
                                </Stack>
                                <Tooltip
                                    title={frontStats.nbOutdated ? (
                                        <Box sx={{ whiteSpace: 'pre-line' }}>
                                            {frontStats.outdatedItems.map(o => `• ${o.instanceName} [${o.branch}] · ${o.application} : ${o.version}`).join('\n')}
                                        </Box>
                                    ) : ''}
                                    placement='bottom-end'
                                    disableHoverListener={!frontStats.nbOutdated}
                                >
                                    <Stack direction='row' spacing={0.75} alignItems='center'>
                                        <Icon sx={{ fontSize: '1.5rem', color: frontStats.nbOutdated ? PASTEL_COLOR.redDark : PASTEL_COLOR.greyDark }}>warning</Icon>
                                        <Typography sx={{ fontSize: '1.1rem', color: frontStats.nbOutdated ? PASTEL_COLOR.redDark : 'inherit' }}>
                                            <strong>{frontStats.nbOutdated}</strong> obsolète{frontStats.nbOutdated > 1 ? 's' : ''}
                                        </Typography>
                                    </Stack>
                                </Tooltip>
                            </Stack>
                        </Grid>
                    )}
                </Grid>
            )}
            {isJobMode && (
                <Grid container alignItems='center' spacing={1}>
                    <Grid size='auto'>
                        <RadioButtons
                            label="Mode d'affichage"
                            value={jobViewMode}
                            items={[
                                { value: DISPLAY_MODE_OPTIONS.TABLE, label: 'Tableaux', icon: 'table_rows' },
                                { value: DISPLAY_MODE_OPTIONS.MAP, label: 'Carte', icon: 'grid_view' },
                            ]}
                            onChange={v => setJobViewMode(v)}
                        />
                    </Grid>
                    <Grid size='auto'>
                        <div style={{ width: 250 }}>
                            <Select
                                label="Type d'erreur"
                                items={jobCategoryItems}
                                value={jobCategoryFilter}
                                onChange={v => setJobCategoryFilter(v)}
                                undefinedValue
                                nullLabel={`Toutes (${totalJobErrors})`}
                            />
                        </div>
                    </Grid>
                    <Grid size='auto'>
                        <div style={{ width: 250 }}>
                            <Select
                                label='Type de traitement'
                                items={jobTypeItems}
                                value={jobTypeFilter}
                                onChange={v => setJobTypeFilter(v)}
                                undefinedValue
                                nullLabel='Tous'
                                sortItems={false}
                            />
                        </div>
                    </Grid>
                </Grid>
            )}
            {showFrontTableView && (
                <BranchVersionsTables
                    instances={instances}
                    frontLatestVersions={frontLatestVersions}
                    onClick={onClick}
                    applicationFilter={applicationFilter}
                />
            )}
            {showJobTableView && (
                <JobsTable instances={instances} categoryFilter={jobCategoryFilter} jobTypeFilter={jobTypeFilter} />
            )}
            {!showFrontTableView && !showJobTableView && (
                <Grid container spacing={1}>
                    {columns.map((datacentersList) => (
                        <Grid size={12 / (columns.filter(c => c.length > 0).length)} key={datacentersList[0]?.server} container alignSelf='flex-start'>
                            {datacentersList.map((datacenter, idx) => {
                                const small = isHalfWidthDatacenter(datacentersList, idx)
                                return (
                                    <Grid size={small ? 6 : 12} key={datacenter.server}>
                                        <DatacenterCard
                                            datacenter={datacenter}
                                            onClick={onClick}
                                            displayMode={displayMode}
                                            settingsView={settingsView}
                                            frontLatestVersions={frontLatestVersions}
                                            jobCategoryFilter={jobCategoryFilter}
                                            jobTypeFilter={jobTypeFilter}
                                        />
                                    </Grid>
                                )
                            })}
                        </Grid>
                    ))}
                </Grid>
            )}
        </Stack>
    )
}

KwaKeUpMap.propTypes = {
    instances: PropTypes.arrayOf(PropTypes.shape({})),
    onClick: PropTypes.func,
    displayMode: PropTypes.string,
    services: PropTypes.arrayOf(PropTypes.shape({})),
    settingsView: PropTypes.shape({}),
}

export default KwaKeUpMap
export { InstanceCard }
